import { Suspense, lazy, ReactNode } from "react";
import CostDashboard from "pages/cost-dashboard";
import { Navigate, RouteObject } from "react-router-dom";
import SidebarLayout from "layouts/SidebarLayout";
import SuspenseLoader from "shared-compoents/SuspenseLoader";
import PersistentDrawerLeft from "pages/docs";
import Requests from "pages/onboarding/Requests";
import UserOnboarding from "../pages/onboarding/UserOnboarding";
import Request from "../pages/onboarding/Request";
import PageTitle from "shared-compoents/page-title";
import SelfServicePortal from "pages/self-service-portal";
import PodOnboarding from "pages/onboarding/PodOnboarding";
import TeamDirectory from "pages/team-directory";
import RaiseInquiry from "pages/raise-inquiry";
import PowerBIDashboard from "pages/dashboard";
import CostAnalyticsDashboard from "pages/cost-analytics-dashboard";
import UnityCatalogDashboard from "pages/unity-catalog";
import DashboardWrapper from "pages/dashboard/DashboardWrapper";

// Loader function to wrap lazy-loaded components
const Loader = (Component: any) => (props: any) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Lazy-loaded pages
const Home = Loader(lazy(() => import("pages/home-page")));
const Overview = Loader(lazy(() => import("pages/overview-page")));
const MlOps = Loader(lazy(() => import("pages/mlops")));
const EndpointCreation = Loader(
  lazy(() => import("pages/mlops/endpointCreation"))
);
const BatchInferencing = Loader(
  lazy(() => import("pages/mlops/batchInferencing"))
);
const Training = Loader(lazy(() => import("pages/mlops/training")));
const Documentation = Loader(lazy(() => import("pages/documentation")));
const Status404 = Loader(
  lazy(() => import("shared-compoents/Status404/index"))
);
const Initialization = Loader(lazy(() => import("pages/mlops/initialization")));
const IntilizationStepper = Loader(lazy(() => import("pages/mlops/stepper")));
const CloudService = Loader(
  lazy(() => import("pages/cloud-service-provisioning/cloud-service"))
);
const ModelRegistry = Loader(lazy(() => import("pages/model-registry")));

const Page = ({
  children,
  pageTitle,
}: {
  children: ReactNode;
  pageTitle?: string;
}) => {
  return (
    <>
      <PageTitle title={pageTitle} />
      {children}
    </>
  );
};

// Define routes using RouteObject[]
const routes: RouteObject[] = [
  {
    path: "",
    element: <SidebarLayout />,
    children: [
      {
        path: "/",
        element: (
          <Page>
            <Home />
          </Page>
        ),
      },
      {
        path: "home",
        element: <Navigate to="/" replace />,
      },
      {
        path: "overview",
        element: (
          <Page pageTitle="Overview">
            <Overview />
          </Page>
        ),
      },
      {
        path: "contactus",
        element: <Overview />,
      },
      {
        path: "*",
        element: <Status404 />,
      },
    ],
  },
  {
    path: "/ds-life-cycle",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="About">
            <MlOps />
          </Page>
        ),
      },
    ],
  },

  {
    path: "templates",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="MLOps Reusable Templates">
            <Initialization />
          </Page>
        ),
      },
      {
        path: "sgm",
        element: <IntilizationStepper />,
      },
      {
        path: "endpointCreation",
        element: <EndpointCreation />,
      },
      {
        path: "batchinferencing",
        element: <BatchInferencing />,
      },
      {
        path: "training",
        element: <Training />,
      },
    ],
  },
  {
    path: "documentation",
    element: <SidebarLayout />,
    children: [
      {
        path: ":pageId",
        element: (
          <Page pageTitle="Platform Docs">
            <Documentation />
          </Page>
        ),
      },
    ],
  },
  {
    path: "docs",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: <PersistentDrawerLeft />,
      },
    ],
  },
  {
    path: "docs2",
    element: <PersistentDrawerLeft />,
  },

  // {
  //   path: "/:onboardId",
  //   element: <OnBoardFlow />,
  // },

  {
    path: "track-requests",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Track Requests">
            <Requests />
          </Page>
        ),
      },
      {
        path: ":requestId",
        element: (
          <Page pageTitle="Request Details">
            <Request />
          </Page>
        ),
      },
    ],
  },
  {
    path: "self-service-portal",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Self Service Portal">
            <SelfServicePortal />
          </Page>
        ),
      },
      {
        path: "user-onboarding",
        element: (
          <Page pageTitle="User Onboarding">
            <UserOnboarding />
          </Page>
        ),
      },
      {
        path: "pod-onboarding",
        element: (
          <Page pageTitle="Pod Onboarding">
            <PodOnboarding />
          </Page>
        ),
      },
      {
        path: "mlops-template/:templateType",
        element: <IntilizationStepper />,
      },
      {
        path: "cloud-services/:cloudService",
        element: <CloudService />,
      },
    ],
  },
  {
    path: "team-directory",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Team Directory">
            <TeamDirectory />
          </Page>
        ),
      },
    ],
  },
  {
    path: "raise-inquiry",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Raise Inquiry">
            <RaiseInquiry />
          </Page>
        ),
      },
    ],
  },
  {
    path: "/tdsp-inventory-dashboard",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Dashboard">
            <PowerBIDashboard />
          </Page>
        ),
      },
    ],
  },
  {
    path: "/tdsp-cost-dashboard",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Cost Dashboard">
            <CostAnalyticsDashboard />
          </Page>
        ),
      },
    ],
  },
  {
    path: "unity-catalog",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Unity Catalog">
            <UnityCatalogDashboard />
          </Page>
        ),
      },
    ],
  },
  {
    path: "new-inventory-dashboard",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Pipeline Metrics">
            <DashboardWrapper />
          </Page>
        ),
      },
    ],
  },
  {
    path: "platform-dashboard",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Platform Dashboard">
            <CostDashboard />
          </Page>
        ),
      },
    ],
  },
  {
    path: "model-registry",
    element: <SidebarLayout />,
    children: [
      {
        path: "",
        element: (
          <Page pageTitle="Model Registry">
            <ModelRegistry />
          </Page>
        ),
      },
    ],
  }
];

export default routes;
